import {
	ColliderNodesByModelId,
	EntityModelIdLookup,
	ModelParser,
} from "@animech/supermodel";
import {
	Entity,
	GraphNode,
	Vec3,
	Quat,
	ShaderChunks,
	StandardMaterial,
} from "playcanvas";
import { Property } from "../../definitions";
import {
	BodyType,
	BodyTypeCollisionPair,
	ColliderDefinition,
	CollisionSystem,
} from "src/scene/collision-system";
import {
	createDynamicColliderNodes,
	DynamicColliderNodeDefinition,
	dynamicColliderNodeDefinitionsByModel,
} from "src/scene/colliders/dynamic-colliders";
import { PropertyChangeMap } from "src/common";

const [hiddenColliderMaterial, setVisiblePassesForHiddenColliderMaterial] =
	createHiddenColliderMaterial();

const defaultBodyType: BodyType = "kinematic";
const defaultBodyTypeCollisionPairs: BodyTypeCollisionPair[] = [
	"kinematic:fixed",
	"kinematic:kinematic",
];

export type CollisionStartCallback = (entity: Entity) => void;
export type CollisionEndCallback = (entity: Entity) => void;

export function applyColliders(
	colliderNodesByModelId: ColliderNodesByModelId,
	collisionSystem: CollisionSystem,
	rootEntity: Entity,
	entityModelIdLookup: EntityModelIdLookup,
	entityModifiedLookup: (entity: Entity) => boolean,
	entityPropertyLookup: (entity: Entity, property: Property) => unknown,
	propertyChanges?: PropertyChangeMap,
) {
	// eslint-disable-next-line unicorn/no-array-for-each
	rootEntity.forEach((entity: GraphNode) => {
		if (!(entity instanceof Entity)) {
			return;
		}
		if (!entity.model) {
			if (collisionSystem.hasEntityBody(entity)) {
				collisionSystem.removeEntityBody(entity);
			}
			return;
		}

		const modelId = entityModelIdLookup(entity);

		if (!modelId) {
			return;
		}

		const dynamicColliderNodeDefinitions:
			| DynamicColliderNodeDefinition[]
			| null = dynamicColliderNodeDefinitionsByModel()[modelId] ?? null;
		const entityIsModified =
			entityModifiedLookup(entity) ||
			dynamicColliderNodeDefinitions?.some((dynamicColliderNodeDefinition) =>
				[...(propertyChanges ?? [])].some((property) =>
					property[0].equals(dynamicColliderNodeDefinition.property),
				),
			);

		// TODO: Identify the dynamic collider property in the changed properties
		const hasChangedProperty = !!dynamicColliderNodeDefinitions;
		const entityHasCollisionBody = collisionSystem.hasEntityBody(entity);
		const needsUpdate =
			!entityHasCollisionBody || entityIsModified || hasChangedProperty;

		if (!needsUpdate) {
			return;
		}

		const colliderNodes =
			(dynamicColliderNodeDefinitions
				? createDynamicColliderNodes(
						(property) => entityPropertyLookup(entity, property),
						dynamicColliderNodeDefinitions,
					)
				: null) ??
			colliderNodesByModelId[modelId] ??
			[];

		if (colliderNodes.length <= 0) {
			return;
		}

		if (collisionSystem.hasEntityBody(entity)) {
			collisionSystem.removeEntityBody(entity);
		}

		collisionSystem.addEntityBody(entity, "kinematic");

		const entityScale = entity.getLocalScale();
		const colliderTranslationScale = new Vec3(
			Math.sign(entityScale.x),
			Math.sign(entityScale.y),
			Math.sign(entityScale.z),
		);

		for (const { errors, transform } of colliderNodes) {
			if (errors?.length) {
				continue;
			}
			addEntityCollider(collisionSystem, entity, {
				type: "box",
				halfExtents: transform.getScale().clone().mulScalar(0.5),
				translation: transform.getTranslation().mul(colliderTranslationScale),
				rotation: new Quat().setFromMat4(transform),
			});
		}
	});
}

export function addEntityCollider(
	collisionSystem: CollisionSystem,
	entity: Entity,
	collider: ColliderDefinition,
	bodyType = defaultBodyType,
	bodyTypeCollisionPairs = defaultBodyTypeCollisionPairs,
) {
	if (collisionSystem.hasEntityBody(entity)) {
		collisionSystem.setEntityBodyType(entity, bodyType);
	} else {
		collisionSystem.addEntityBody(entity, bodyType);
	}
	collisionSystem.addEntityBodyCollider(
		entity,
		collider,
		bodyTypeCollisionPairs,
	);
}

export function hideColliderMeshes(
	rootEntity: Entity,
	visibleInShaderPasses: number[] = [],
) {
	setVisiblePassesForHiddenColliderMaterial(visibleInShaderPasses);

	// eslint-disable-next-line unicorn/no-array-for-each
	rootEntity.forEach((entity: GraphNode) => {
		if (!(entity instanceof Entity) || !entity.model) {
			return;
		}

		for (const meshInstance of entity.model.meshInstances ?? []) {
			if (ModelParser.colliderNodeNameFilter(meshInstance.node.name)) {
				if (visibleInShaderPasses.length > 0) {
					meshInstance.castShadow = false;
					meshInstance.material = hiddenColliderMaterial;
				} else {
					meshInstance.visible = false;
				}
			}
		}
	});
}

export function isSpecialModelParserNode(node: GraphNode) {
	const { name } = node;
	return (
		ModelParser.attacherNodeNameFilter(name) ||
		ModelParser.colliderNodeNameFilter(name) ||
		ModelParser.morphNodeNameFilter(name)
	);
}

function createHiddenColliderMaterial() {
	const material = new StandardMaterial();
	const hiddenShaderChunks = createDiscardShaderChunks();
	material.useFog = false;
	material.useSkybox = false;
	material.useLighting = false;
	material.useMetalness = false;
	material.useTonemap = false;
	material.diffuse.set(0, 0, 0, 0);
	material.emissive.set(1, 0, 0, 1);

	let visibleShaderPasses: number[] = [];

	const setVisibleShaderPasses = (passes: number[]) => {
		visibleShaderPasses = passes;
	};

	material.onUpdateShader = function (options) {
		const { pass, litOptions } = options;

		if (!visibleShaderPasses.includes(pass)) {
			litOptions.shaderChunks = hiddenShaderChunks;
		}
		return options;
	};

	return [material, setVisibleShaderPasses] as const;
}

function createDiscardShaderChunks(): ShaderChunks {
	const shaderChunks = new ShaderChunks();
	shaderChunks.version = "2.14";
	shaderChunks.glsl.set("outputPS", "discard;");
	shaderChunks.wgsl.set("outputPS", "discard;");
	return shaderChunks;
}
